import { SectionTypeEnum } from '@vedalib/editor/lib/elements'
import { generatePath } from 'react-router'

import * as s from 'components/FileUsage/FileUsageComponent.module.scss'
import { Tooltip } from 'components/uiKit/Dropdown'
import type { KeyIconsType } from 'components/uiKit/Icon'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import Link from 'components/uiKit/Link'
import { EDITOR_PATHS } from 'constants/paths'
import type { UsageBlockItem } from 'gql/__generated__/graphql'
import { t } from 'services/Translation'

import { FileUsageState, FlattenUsageState, FlattUsageTypeEnum } from './types'

export const iconsMap: Record<SectionTypeEnum, KeyIconsType> = {
  [SectionTypeEnum.cover]: 'contentsSlides',
  [SectionTypeEnum.landing]: 'contentsLanding',
  [SectionTypeEnum.chapter]: 'contentsChapter',
  [SectionTypeEnum.test]: 'contentsTest',
}

const generatePathWithQueryParams = (
  path: string,
  params: Record<string, string>,
  queryParams?: Record<string, string | null | undefined>,
): string => {
  const pathName = generatePath(path, params)
  if (!queryParams) {
    return pathName
  }
  const queryString = Object.entries(queryParams)
    .filter(([, value]) => value !== undefined && value !== null)
    .map(([key, value]) => `${key}=${value}`)
    .join('&')
  return `${pathName}?${queryString}`
}

const sortUsage = (groupedUsages: FileUsageState[]): FileUsageState[] => {
  const sortedUsages = [...groupedUsages]
  sortedUsages.forEach((group) => {
    group.sections.sort((a, b) => {
      return (
        group.sectionsOrder.findIndex(({ id }) => id === a.id) -
        group.sectionsOrder.findIndex(({ id }) => id === b.id)
      )
    })
    group.sections.forEach((section) => {
      section.blocks.sort((a, b) => {
        return section.blocksOrder.indexOf(a.id) - section.blocksOrder.indexOf(b.id)
      })
    })
  })

  return sortedUsages
}

export const groupUsage = (fileUsages: UsageBlockItem[], companyId: string): FileUsageState[] => {
  const groupedUsages: FileUsageState[] = []
  fileUsages.forEach((usage) => {
    if (!usage.project || !usage.section) {
      return
    }
    const projectIndex = groupedUsages.findIndex((group) => group.projectId === usage.project?.id)
    if (projectIndex === -1) {
      groupedUsages.push({
        projectName: usage.project.name,
        projectId: usage.project.id,
        sectionsOrder: usage.project.sectionsOrder,
        sections: [
          {
            id: usage.section.id,
            sectionName: usage.section.name,
            sectionType: usage.section.type as SectionTypeEnum,
            blocksOrder: usage.section.blocksOrder,
            blocks: [
              {
                id: usage.blockUuid,
                blockName: usage.blockName,
                usageId: usage.fileUsageId,
                isCropped: usage.isCropped,
                context: usage.context,
                blockUrl: generatePathWithQueryParams(
                  EDITOR_PATHS.main,
                  {
                    companyId,
                    projectId: usage.project.id,
                    sectionId: usage.section.id,
                    blockId: usage.blockUuid,
                  },
                  {
                    nodeId: usage.context?.nodeId,
                  },
                ),
              },
            ],
          },
        ],
      })
    } else {
      const sectionIndex = groupedUsages[projectIndex].sections.findIndex(
        (section) => section.sectionName === usage.section?.name,
      )
      if (sectionIndex === -1) {
        groupedUsages[projectIndex].sections.push({
          id: usage.section.id,
          sectionName: usage.section.name,
          sectionType: usage.section.type as SectionTypeEnum,
          blocksOrder: usage.section.blocksOrder,
          blocks: [
            {
              blockName: usage.blockName,
              id: usage.blockUuid,
              usageId: usage.fileUsageId,
              isCropped: usage.isCropped,
              context: usage.context,
              blockUrl: generatePathWithQueryParams(
                EDITOR_PATHS.main,
                {
                  companyId,
                  projectId: usage.project.id,
                  sectionId: usage.section.id,
                  blockId: usage.blockUuid,
                },
                {
                  nodeId: usage.context?.nodeId,
                },
              ),
            },
          ],
        })
      } else {
        groupedUsages[projectIndex].sections[sectionIndex].blocks.push({
          blockName: usage.blockName,
          id: usage.blockUuid,
          usageId: usage.fileUsageId,
          context: usage.context,
          blockUrl: generatePathWithQueryParams(
            EDITOR_PATHS.main,
            {
              companyId,
              projectId: usage.project.id,
              sectionId: usage.section.id,
              blockId: usage.blockUuid,
            },
            {
              nodeId: usage.context?.nodeId,
            },
          ),
          isCropped: usage.isCropped,
        })
      }
    }
  })

  return sortUsage(groupedUsages)
}

export const flattenUsage = (groupedUsage: FileUsageState): FlattenUsageState[] => {
  const flattenUsage: FlattenUsageState[] = []
  flattenUsage.push({
    name: groupedUsage.projectName,
    type: FlattUsageTypeEnum.project,
    icon: 'mainMenuProjects',
  })
  groupedUsage.sections.forEach((section) => {
    flattenUsage.push({
      name: section.sectionName,
      type: FlattUsageTypeEnum.section,
      icon: iconsMap[section.sectionType],
    })
    section.blocks.forEach((block) => {
      const elementTypeTranslationKey =
        block.context?.elementType && `elements.${block.context.elementType}.label`

      const elementName =
        block.context?.name ||
        (block.context?.isBackground
          ? t('editorV2.background.name').toLowerCase()
          : block.context?.isBorder
            ? t('editorV2.border.name').toLowerCase()
            : elementTypeTranslationKey && t(elementTypeTranslationKey).toLowerCase())

      flattenUsage.push({
        blockName: block.blockName,
        type: FlattUsageTypeEnum.block,
        name: (
          // eslint-disable-next-line react/forbid-component-props
          <Link className={s.blockLink} name={block.blockName} target='_blank' to={block.blockUrl}>
            {block.isCropped && (
              <div className={s.iconWrapper}>
                <Tooltip overlay={t('uiKit.fileUsage.croppedTooltip')}>
                  <Icon name='crop' size={KitSize.XS} />
                </Tooltip>
              </div>
            )}
            {block.context?.isStash && (
              <div className={s.iconWrapper}>
                <Tooltip overlay={t('uiKit.fileUsage.stashTooltip')}>
                  <Icon name='stash' size={KitSize.XS} />
                </Tooltip>
              </div>
            )}
            <Tooltip overlay={t('uiKit.fileUsage.blockTooltip')}>
              {block.blockName}
              {elementName && `, ${elementName}`}
            </Tooltip>
          </Link>
        ),
      })
    })
  })

  return flattenUsage
}
